import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_MAX_HEIGHT = 280;
const VIEWPORT_GAP = 8;

const SearchableSelect = ({
  id,
  value,
  onChange,
  options = [],
  placeholder = '— Sélectionner —',
  emptyLabel = 'Aucun résultat',
  allowEmpty = false,
  emptyOptionLabel = '—',
  disabled = false,
  required = false,
  className = '',
  buttonClassName = 'form-select text-start',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const listId = useId();

  const normalizedOptions = useMemo(
    () => options.map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt)),
    [options],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return normalizedOptions;
    }

    return normalizedOptions.filter((opt) => {
      const label = String(opt.label ?? '').toLowerCase();
      const description = String(opt.description ?? '').toLowerCase();
      const optionValue = String(opt.value ?? '').toLowerCase();
      return label.includes(q) || description.includes(q) || optionValue.includes(q);
    });
  }, [normalizedOptions, search]);

  const selectedLabel = normalizedOptions.find((opt) => String(opt.value) === String(value))?.label
    ?? (value ? String(value) : '');

  const updateMenuPosition = useCallback(() => {
    const trigger = rootRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_GAP;
    const spaceAbove = rect.top - VIEWPORT_GAP;
    const openUpward = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      MENU_MAX_HEIGHT,
      Math.max(140, openUpward ? spaceAbove : spaceBelow),
    );
    const width = Math.max(rect.width, 220);

    setMenuStyle({
      position: 'fixed',
      left: Math.min(rect.left, window.innerWidth - width - VIEWPORT_GAP),
      width,
      zIndex: 2000,
      maxHeight,
      ...(openUpward
        ? { bottom: viewportHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }

    updateMenuPosition();

    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);

    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateMenuPosition, filtered.length, search]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      const inTrigger = rootRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        setOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
    setSearch('');
  };

  const toggleOpen = () => {
    if (disabled) {
      return;
    }
    setOpen((prev) => !prev);
    if (open) {
      setSearch('');
    }
  };

  const menu = open && menuStyle
    ? createPortal(
      <div
        ref={menuRef}
        className="searchable-select__menu border rounded shadow-sm bg-base"
        style={menuStyle}
      >
        <div className="p-2 border-bottom">
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <ul
          id={listId}
          role="listbox"
          className="list-unstyled mb-0 overflow-auto"
          style={{ maxHeight: Math.max(100, (menuStyle.maxHeight || MENU_MAX_HEIGHT) - 52) }}
        >
          {allowEmpty && !search.trim() && (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={!value}
                className={`w-100 text-start border-0 px-3 py-2 ${
                  !value ? 'bg-light fw-medium' : 'bg-transparent text-secondary-light'
                }`}
                onClick={() => handleSelect('')}
              >
                {emptyOptionLabel}
              </button>
            </li>
          )}
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-secondary-light small">{emptyLabel}</li>
          ) : (
            filtered.map((opt) => (
              <li key={String(opt.value)}>
                <button
                  type="button"
                  role="option"
                  aria-selected={String(opt.value) === String(value)}
                  disabled={Boolean(opt.disabled)}
                  className={`w-100 text-start border-0 px-3 py-2 ${
                    String(opt.value) === String(value) ? 'bg-light fw-medium' : 'bg-transparent'
                  } ${opt.disabled ? 'text-secondary-light' : ''} ${
                    opt.description ? 'searchable-select__option--rich' : ''
                  }`}
                  onClick={() => {
                    if (!opt.disabled) {
                      handleSelect(opt.value);
                    }
                  }}
                >
                  <span className="searchable-select__option-main d-flex align-items-start justify-content-between gap-2">
                    <span className="searchable-select__option-label">{opt.label}</span>
                    {opt.badge ? (
                      <span className="searchable-select__option-badge">{opt.badge}</span>
                    ) : null}
                  </span>
                  {opt.description ? (
                    <span className="searchable-select__option-desc d-block">{opt.description}</span>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>,
      document.body,
    )
    : null;

  return (
    <div ref={rootRef} className={`searchable-select position-relative ${className}`}>
      <button
        type="button"
        id={id}
        className={`searchable-select__trigger ${buttonClassName} ${!value ? 'text-secondary-light' : ''}`}
        onClick={toggleOpen}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
        title={value ? selectedLabel : undefined}
      >
        <span className="searchable-select__label">
          {value ? selectedLabel : placeholder}
        </span>
      </button>

      {required && (
        <input
          tabIndex={-1}
          aria-hidden
          value={value}
          required
          readOnly
          onChange={() => {}}
          style={{
            position: 'absolute',
            opacity: 0,
            height: 0,
            width: 0,
            pointerEvents: 'none',
          }}
        />
      )}

      {menu}
    </div>
  );
};

export default SearchableSelect;
